import { Check } from 'lucide-react';
import type { NotificationItem } from './types';
import { featureToIcon } from './featureMap';
import { formatWhen } from './dateGroups';
import { useNotifications } from './useNotifications';
import { cn } from '../lib/utils';

interface Props {
  item: NotificationItem;
  /** Open this notification's target in the pane (provided by TaskPane). */
  onOpen: (item: NotificationItem) => void;
}

export function NotificationRow({ item, onOpen }: Props): JSX.Element {
  const { markRead } = useNotifications();
  const Icon = featureToIcon(item.feature);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
      className={cn(
        'flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors',
        item.read ? 'bg-background hover:bg-muted/50' : 'bg-blue-50 hover:bg-blue-100/70',
      )}
    >
      <div className="mt-0.5 flex-shrink-0">
        <Icon className={cn('h-4 w-4', item.read ? 'text-muted-foreground' : 'text-primary')} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {!item.read && <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />}
          <p
            className={cn(
              'truncate text-sm',
              item.read ? 'font-normal text-foreground' : 'font-semibold text-foreground',
            )}
          >
            {item.title || 'Notification'}
          </p>
        </div>
        {item.description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.description}</p>
        )}
        <p className="mt-1 text-[11px] text-muted-foreground/70">{formatWhen(item.createdAt)}</p>
      </div>
      {!item.read && (
        <button
          type="button"
          aria-label="Mark as read"
          title="Mark as read"
          className="flex-shrink-0 rounded p-1 text-muted-foreground hover:bg-background hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            void markRead(item.id);
          }}
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
